import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Users, BookOpen, CreditCard, TrendingUp, Award, DollarSign } from "lucide-react";

export default function AnalyticsPage() {
  const location = useLocation();

  const getActiveTab = () => {
    if (location.pathname.includes("/tests")) return "tests";
    if (location.pathname.includes("/payments")) return "payments";
    return "users";
  };

  const [activeTab, setActiveTab] = useState(getActiveTab);

  return (
    <div>
      {/* Top Metric Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <div className="admin-card" style={{ marginBottom: 0, padding: "18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>Total Registered Aspirants</span>
            <Users size={18} style={{ color: "#0284c7" }} />
          </div>
          <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>14,280</div>
          <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>+12% this month</span>
        </div>

        <div className="admin-card" style={{ marginBottom: 0, padding: "18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>Test Attempts Recorded</span>
            <BookOpen size={18} style={{ color: "#16a34a" }} />
          </div>
          <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>48,912</div>
          <span style={{ fontSize: "12px", color: "#64748b" }}>Avg Score: 64.2%</span>
        </div>

        <div className="admin-card" style={{ marginBottom: 0, padding: "18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>Active Paid Subscriptions</span>
            <CreditCard size={18} style={{ color: "#9333ea" }} />
          </div>
          <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>3,845</div>
          <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>+8.4% renewal rate</span>
        </div>

        <div className="admin-card" style={{ marginBottom: 0, padding: "18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>MRR (Est. Revenue)</span>
            <DollarSign size={18} style={{ color: "#f59e0b" }} />
          </div>
          <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>₹ 18.4L</div>
          <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>Consistent growth</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-card" style={{ padding: "0", overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--admin-border)", background: "#f8fafc" }}>
          {[
            { key: "users", label: "User Analytics" },
            { key: "tests", label: "Test Analytics" },
            { key: "payments", label: "Payments / Subscriptions" },
          ].map((tab) => (
            <button
              key={tab.key}
              style={{
                padding: "14px 24px",
                border: "none",
                background: activeTab === tab.key ? "#ffffff" : "transparent",
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? "#0284c7" : "#64748b",
                borderBottom: activeTab === tab.key ? "2px solid #0284c7" : "none",
                cursor: "pointer",
                fontSize: "14px",
              }}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: "24px" }}>
          {activeTab === "users" && (
            <div>
              <h4 style={{ fontSize: "15px", fontWeight: 700, marginBottom: "14px", color: "#1e293b" }}>
                User Engagement & Demographics
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px" }}>Daily Active Users (DAU)</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: "#0284c7" }}>4,120</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>Peak hours: 7 PM - 10 PM IST</div>
                </div>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px" }}>Average Session Duration</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: "#16a34a" }}>34 mins</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>High engagement on prelims tests</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "tests" && (
            <div>
              <h4 style={{ fontSize: "15px", fontWeight: 700, marginBottom: "14px", color: "#1e293b" }}>
                Test Completion & Accuracy Metrics
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px" }}>Top Attempted Test</div>
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>GS Mini Mock 1</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>1,840 attempts &bull; Avg score 54%</div>
                </div>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px" }}>Hardest Subject (Lowest Accuracy)</div>
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#dc2626" }}>Environment & Ecology</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>Average accuracy: 44.8%</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "payments" && (
            <div>
              <h4 style={{ fontSize: "15px", fontWeight: 700, marginBottom: "14px", color: "#1e293b" }}>
                Active Subscription Plans Distribution
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>Prelims Test Series</div>
                  <div style={{ fontSize: "20px", fontWeight: 700, color: "#0284c7" }}>2,140</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>₹ 2,999 / user</div>
                </div>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>Premium Monthly</div>
                  <div style={{ fontSize: "20px", fontWeight: 700, color: "#16a34a" }}>950</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>₹ 999 / month</div>
                </div>
                <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid var(--admin-border)" }}>
                  <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>Premium Yearly</div>
                  <div style={{ fontSize: "20px", fontWeight: 700, color: "#9333ea" }}>755</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>₹ 8,999 / year</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
